import type { ReactNode, Ref } from "react";
import Link from "next/link";
import { Breadcrumbs, type BreadcrumbItemType } from "@/components/ui/Breadcrumbs";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { MobileMapToggle } from "@/components/location/MobileMapToggle";
import { BelowLgOnly } from "@/components/responsive/BreakpointGate";
import { quoteHrefFor } from "@/lib/listings/category-facets";
import { cn } from "@/lib/utils";

export function CatalogueShell({
  header,
  filterBar,
  mapColumn,
  paneRef,
  after,
  children,
}: {
  header?: ReactNode;
  filterBar?: ReactNode;
  mapColumn?: ReactNode;
  paneRef?: Ref<HTMLElement>;
  after?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col bg-background">
      {header}
      <div className="flex">
        {mapColumn}
        <div className="flex min-w-0 flex-1 flex-col">
          {filterBar ? (
            <div className="sticky top-16 z-20 max-h-[calc(100dvh-4rem)] overflow-y-auto bg-background">{filterBar}</div>
          ) : null}
          <section
            ref={paneRef}
            className="flex min-w-0 flex-1 flex-col [overflow-anchor:none]"
          >
            {children}
          </section>
        </div>
      </div>
      {after}
    </div>
  );
}

const NAVBAR_HEIGHT_PX = 64;

export function scrollCatalogueToTop(pane: HTMLElement | null) {
  if (!pane) return;
  const stickyBar = pane.previousElementSibling;
  const stickyBottom =
    stickyBar instanceof HTMLElement
      ? stickyBar.getBoundingClientRect().bottom
      : NAVBAR_HEIGHT_PX;
  const offset = pane.getBoundingClientRect().top - stickyBottom;
  if (offset < 0) window.scrollBy({ top: offset });
}

export function CatalogueHeaderBar({
  breadcrumbs,
  action,
}: {
  breadcrumbs: BreadcrumbItemType[];
  action?: ReactNode;
}) {
  return (
    <div className="flex shrink-0 items-center justify-between gap-3 border-b border-border px-4 py-2">
      <Breadcrumbs items={breadcrumbs} />
      {action}
    </div>
  );
}

export function CatalogueQuoteCard({ productSlug }: { productSlug: string }) {
  return (
    <Card className="m-3 sm:m-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <p className="font-semibold text-foreground">Need a size, grade or volume you don&apos;t see here?</p>
        <p className="text-sm text-muted-foreground">Tell us what you need and we&apos;ll quote it delivered.</p>
      </div>
      <Button asChild variant="outline" className="shrink-0">
        <Link href={quoteHrefFor(productSlug)}>Check availability</Link>
      </Button>
    </Card>
  );
}

export function CatalogueMapColumn({ children }: { children: ReactNode }) {
  return (
    <aside
      aria-label="Listings on map"
      className="sticky top-16 hidden h-[calc(100dvh-4rem)] w-1/3 max-w-120 shrink-0 self-start border-r border-border lg:block"
    >
      {children}
    </aside>
  );
}

export function CatalogueMobileMap({
  overlayTitle,
  open,
  onOpenChange,
  children,
}: {
  overlayTitle: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: ReactNode;
}) {
  return (
    <BelowLgOnly>
      <MobileMapToggle
        pillLabel="Map"
        overlayTitle={overlayTitle}
        open={open}
        onOpenChange={onOpenChange}
        className="h-[320px] shrink-0 border-b border-border"
      >
        {children}
      </MobileMapToggle>
    </BelowLgOnly>
  );
}

export function CatalogueResultsHeading({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-x-3 gap-y-1 border-b border-border px-4 py-3 max-md:py-2">
      <div className="min-w-0">
        <h1 className="text-lg font-bold text-foreground max-md:text-base sm:text-xl">{title}</h1>
        <p className="mt-0.5 text-sm text-muted-foreground tabular-nums max-md:mt-0 max-md:text-xs" aria-live="polite">
          {subtitle}
        </p>
      </div>
      {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
    </div>
  );
}

export function catalogueGridClassName(mapVisible: boolean): string {
  return cn(
    "grid grid-cols-1 md:grid-cols-2 md:gap-4 md:p-4",
    mapVisible
      ? "xl:grid-cols-3"
      : "lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5",
  );
}
